 
import React from "react";
import SFBaseThumbnail from "./base";
import * as SFHelpers from "../../utils";
import SFuseStyled from "../../hooks/useStyled";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import { Box } from "@mui/material";
import { getRGBColorByHex } from "../../services/colorsService";
const { styled } = SFuseStyled();

const ThumbnailWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "canHover",
})(({ theme, canHover }) => ({
  position: "absolute",
  width: "100%",
  height: "100%",
  "& .content": {
    opacity: 1,
    transition: "opacity 400ms",
  },
  "& .content-hover": {
    opacity: 0,
    transition: "opacity 400ms",
    background:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.50), rgba(0, 0, 0, 0.80))",
  },
  ...(canHover === "true" && {
    "&:hover .content": {
      opacity: 0,
    },
    "&:hover .content-hover": {
      opacity: 1,
    },
  }),
}));

const SkillsPathIndicatorContainer = styled(SFStack)(({ theme }) => ({
  borderRadius: "15px",
  background: "rgba(35, 35, 47, 0.60)",
  padding : theme.spacing(0.5,1),
}));

const ContentContainer = styled(SFStack)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  padding: theme.spacing(1),
  "&.content-hover": {
    textAlign: "left",
  },
}));

const BadgeContainer = styled(SFStack)(({ theme, color }) => ({
  position: "absolute",
  left: theme.spacing(0.5),
  bottom: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette[color].main,
  borderRadius: theme.spacing(0.5),
}));

const IconContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: theme.spacing(6),
  height: theme.spacing(6),
  borderRadius: "50%",
  backgroundColor: getRGBColorByHex(theme.palette.black.main, 0.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const TitleStyled = styled(SFTypography)(({ theme }) => ({
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const DescriptionStyled = styled(SFTypography)(({ theme }) => ({
  overflow: "hidden",
  wordWrap: "break-word",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 4,
  lineClamp: 4,
  WebkitBoxOrient: "vertical",
}));

const StyledTypography = styled(SFTypography)(({ theme, lines }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: `${lines}`,
  WebkitBoxOrient: "vertical",
}));

const Stats = ({ items, justifyContent = "center" }) =>
  items.length > 0 && (
    <SFStack direction="row" justifyContent={justifyContent} spacing={2}>
      {items.map((item, index) => (
        <SFStack key={index} direction="row" spacing={0.5} alignItems="center">
          {item.icon}
          {item.total && (
            <SFTypography variant="courseCardDuration">
              {item.total}
            </SFTypography>
          )}
          {item.label && (
            <SFTypography variant="body1">
              {item.label}
            </SFTypography>
          )}
        </SFStack>
      ))}
    </SFStack>
  );

const SFSlideThumbnail = ({
  image = null,
  lazyLoad = false,
  onClick = null,
  badge = null,
  title = null,
  description = null,
  stats = [],
  hoverStats = [],
  icon = null,
  hasHover = false,
  scaleOnHover = false,
  hasMinWidth = true,
  className = "",
  indicator = null,
}) => (
  <SFBaseThumbnail
    onClick={onClick}
    image={image}
    lazyLoad={lazyLoad}
    scaleOnHover={scaleOnHover}
    hasMinWidth={hasMinWidth}
    label={title}
    className={className}
  >
    <ThumbnailWrapper canHover={hasHover.toString()}>
      <ContentContainer
        spacing={1}
        alignItems="center"
        justifyContent="center"
        className="content"
      >
        {title && (
          <StyledTypography variant="body2" lines={3}>
            {title}
          </StyledTypography>
        )}
        <Stats items={stats} />
        {indicator && 
          (<SkillsPathIndicatorContainer>
            <SFTypography variant="body1">
              {indicator}
            </SFTypography>
          </SkillsPathIndicatorContainer>)}
        {icon && <IconContainer>{icon}</IconContainer>}
        {badge && (
          <BadgeContainer
            direction="row"
            spacing={0.5}
            alignItems="center"
            color={badge.color}
          >
            {badge.icon}
            <SFTypography variant="body1">
              {badge.label}
            </SFTypography>
          </BadgeContainer>
        )}
      </ContentContainer>
      {hasHover && (
        <ContentContainer
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
          className="content-hover"
        >
          {indicator && 
          (<SkillsPathIndicatorContainer>
            <SFTypography variant="body1">
              {indicator}
            </SFTypography>
          </SkillsPathIndicatorContainer>)}
          <TitleStyled variant="courseCardHoverTitle">{title}</TitleStyled>
          <DescriptionStyled variant="body1">
            {description && SFHelpers.CleanAndLimitString(description, 150)}
          </DescriptionStyled>
          <Stats items={hoverStats} />
        </ContentContainer>
      )}
    </ThumbnailWrapper>
  </SFBaseThumbnail>
);

export default SFSlideThumbnail;
